import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /** @type {number} */
  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10);
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '';
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(StandardEvents.cartLinesUpdate, this.onNativeCartUpdate);
    document.addEventListener('cart:refresh', this.refreshCartCount);
    window.addEventListener('pageshow', this.onPageShow);
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(StandardEvents.cartLinesUpdate, this.onNativeCartUpdate);
    document.removeEventListener('cart:refresh', this.refreshCartCount);
    window.removeEventListener('pageshow', this.onPageShow);
  }

  /**
   * Handles the theme's own cart update event. We only use event.promise
   * as a signal that the mutation has actually finished server-side —
   * its resolved payload shape isn't something we rely on, since we
   * always re-fetch /cart.js ourselves afterward for a stable, known shape.
   * @param {CartLinesUpdateEvent} event - The cart update event.
   */
  onNativeCartUpdate = (event) => {
    event.promise
      ?.then(() => {
        this.refreshCartCount();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-icon] Event promise rejected:', error);
      });
  };

  /**
   * Plant Pot Configurator: re-fetches the cart fresh (no caching) and
   * recomputes the bubble count. Used both after our own custom
   * /cart/add.js calls (which never fire the theme's native event) and
   * after the theme's own event confirms its mutation has completed.
   */
  refreshCartCount = () => {
    fetch('/cart.js', { cache: 'no-store' })
      .then((res) => res.json())
      .then((cart) => {
        const itemCount = this.computeEffectiveCartCount(cart) ?? cart?.item_count ?? 0;
        this.renderCartBubble(itemCount);
      })
      .catch((error) => {
        console.warn('[cart-icon] Failed to refresh cart count:', error);
      });
  };

  /**
   * Handles the page show event when the page is restored from cache.
   * @param {PageTransitionEvent} event - The page show event.
   */
  onPageShow = (event) => {
    if (event.persisted) {
      this.ensureCartBubbleIsCorrect();
    }
  };

  /**
   * Plant Pot Configurator: computes the "real" number of products in the
   * cart, treating every group of lines that share the same hidden
   * _bundle_id property (base + its pots) as a single product, since
   * that's what it actually is — one configured plant pot stand. Normal
   * (non-bundled) items still count their quantity as usual.
   * @param {object} [cart] - The Ajax cart object, if available.
   * @returns {number|null} The corrected item count, or null if cart.items isn't available.
   */
  computeEffectiveCartCount = (cart) => {
    if (!cart || !Array.isArray(cart.items)) return null;

    const baseBundleIds = new Set();
    let count = 0;

    // First pass: sum base line quantities (a bundle counts as however
    // many the customer set the configuration's own quantity to).
    for (const item of cart.items) {
      const bundleId = item.properties && item.properties['_bundle_id'];
      const bundleRole = item.properties && item.properties['_bundle_role'];
      if (bundleId && bundleRole === 'base') {
        count += item.quantity;
        baseBundleIds.add(bundleId);
      }
    }

    // Second pass: normal items count normally; orphaned pot lines (base
    // missing) fall back to counting themselves so nothing disappears.
    for (const item of cart.items) {
      const bundleId = item.properties && item.properties['_bundle_id'];
      const bundleRole = item.properties && item.properties['_bundle_role'];
      if (!bundleId) {
        count += item.quantity;
      } else if (bundleRole === 'pot' && !baseBundleIds.has(bundleId)) {
        count += item.quantity;
      }
    }

    return count;
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The absolute number of items in the cart.
   * @param {boolean} [animate=true] - Whether to animate the bubble.
   */
  renderCartBubble = async (itemCount, animate = true) => {
    this.refs.cartBubbleCount.classList.toggle('hidden', itemCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', itemCount === 0);

    this.currentCartCount = itemCount;

    this.classList.toggle('header-actions__cart-icon--has-cart', itemCount > 0);

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(this.currentCartCount),
        timestamp: Date.now(),
      })
    );

    if (!animate || itemCount === 0) return;

    // Ensure element is visible before starting animation
    // Use requestAnimationFrame to ensure the browser sees the state change
    await new Promise((resolve) => requestAnimationFrame(resolve));

    this.refs.cartBubble.classList.add('cart-bubble--animating');
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    // Ensure refs are available
    if (!this.refs.cartBubbleCount) return;

    const sessionStorageCount = sessionStorage.getItem('cart-count');

    // If no session storage data, nothing to check
    if (sessionStorageCount === null) return;

    const visibleCount = this.refs.cartBubbleCount.textContent;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);

      // Check if the stored count matches what's visible
      if (value === visibleCount) return;

      // Only update if timestamp is recent (within 10 seconds)
      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);

        if (count >= 0) {
          this.renderCartBubble(count, false);
        }
      }
    } catch (_) {
      // no-op
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}